import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

/**
 * Schema for exclusion response from the API
 */
const exclusionSchema = z.object({
  _id: z.string(),
  agent: z.object({
    _id: z.string(),
    name: z.string(),
  }),
  facility: z.object({
    _id: z.string(),
    name: z.string(),
  }),
  excludedBy: z.string().optional(),
  actionBy: z.enum(["AGENT", "FACILITY"]),
  notes: z.string().optional(),
  reason: z.string().optional(),
  shiftId: z.string().optional(),
  shiftReviewId: z.string().optional(),
  archived: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

const exclusionsResponseSchema = z.array(exclusionSchema);

export type Exclusion = z.infer<typeof exclusionSchema>;

export interface UseWorkerBlockedStatusParams {
  workplaceId: string;
  workerId: string;
  enabled?: boolean;
}

export interface WorkerBlockedStatus {
  isBlocked: boolean;
  exclusion?: Exclusion;
}

/**
 * Hook to check if a worker is blocked/excluded from a workplace.
 * Returns the blocked status and exclusion details if blocked.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useWorkerBlockedStatus({
 *   workplaceId: "facility123",
 *   workerId: "worker456",
 * });
 *
 * if (data?.isBlocked) {
 *   return <ChatBlockedState exclusion={data.exclusion} />;
 * }
 * ```
 */
export function useWorkerBlockedStatus(params: UseWorkerBlockedStatusParams) {
  const { workplaceId, workerId, enabled = true } = params;

  return useQuery({
    queryKey: ["chat", "exclusion", workplaceId, workerId],
    queryFn: async (): Promise<WorkerBlockedStatus> => {
      const response = await get({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/exclusion`,
        queryParams: {
          facilityId: workplaceId,
          agentId: workerId,
        },
        responseSchema: exclusionsResponseSchema,
      });

      // Active exclusions are not archived
      const activeExclusion = response.data.find((exclusion: Exclusion) => !exclusion.archived);

      return {
        isBlocked: Boolean(activeExclusion),
        exclusion: activeExclusion,
      };
    },
    enabled: enabled && Boolean(workplaceId) && Boolean(workerId),
  });
}
